import { Dispatch } from 'redux';
import delve from 'dlv';

import { IIterator } from 'types/shared';
import kyWithSentry from './helper/kyWithSentry';
import { EntityTypes } from 'context/entityContext';
import { addEntities, shouldLoadEntity } from 'redux/reducers/entity';
import {
  apiTypeToEntityType,
  entityTypeToApiType,
} from 'helpers/mapEntityTypes';
import getByUrl from './helper/getByUrl';
import getLatestCertificatesUrl from 'url-constructors/Certification/getLatestCertificatesURL';
import getActualityUrl from 'url-constructors/getActualityURL';

const entityTypes = ['track', 'album']; // available for certification

const generateFetches = (types: IIterator, ids: IIterator): IIterator => {
  return types.reduce((fetches: Record<string, unknown>, type: EntityTypes) => {
    if (ids[type]) {
      const fetchItem = kyWithSentry
        .post(
          `/api/${entityTypeToApiType(type)}/byIds?expand=none`,
          { json: ids[type] } as IIterator
        )
        .json();
      return { ...fetches, [`${type}`]: fetchItem };
    }
    return fetches;
  }, {});
};

const resolveFetches = async (
  allFetches: IIterator,
  dispatch: Dispatch
): Promise<any[]> => {
  const currentEntities: any[] = [];
  for (const type in allFetches) {
    const entities = await allFetches[type];
    const actualEntities = entities.filter(
      (item: any): false | any => delve(item, 'code') !== 404 && item.entity
    );

    if (actualEntities.length > 0) {
      dispatch(addEntities(apiTypeToEntityType(type), actualEntities));
    }

    currentEntities.push(...actualEntities);
  }
  return currentEntities;
};

const filterEntities = (entities: any): IIterator => {
  const ids: IIterator = {};
  entities.forEach((item: { type: EntityTypes; id: string }): void => {
    /** Check if the followed item isn't already stored in Redux or has an error state */
    if (shouldLoadEntity(item.type, item.id)) {
      if (!ids[item.type]) {
        ids[item.type] = [];
      }

      /** Push the item to a list of entityIds that still need to be loaded */
      ids[item.type].push(item.id);
    }
  });
  return ids;
};

const topCertificationsInterceptor = async (
  input: RequestInfo /* eslint-disable-line */,
  init: RequestInit | undefined /* eslint-disable-line */,
  interceptionData?: any
): Promise<Response> => {
  try {
    const { certParams, dispatch } = interceptionData;
    const latestCertificates = await getByUrl<any>(
      getLatestCertificatesUrl(certParams)
    );
    const actuality = await getByUrl<any>(getActualityUrl('streaming'));

    const certs = latestCertificates?.data;

    const entities = certs.map((c: any) => ({
      type: c.entityType,
      id: c.entityId,
    }));

    let currentEntities = [];
    if (entities && entities.length) {
      const idsPerEntityType = filterEntities(entities);
      const fetches = generateFetches(entityTypes, idsPerEntityType);
      currentEntities = await resolveFetches(fetches, dispatch);
    }
    return new Response(
      JSON.stringify({
        entities: currentEntities,
        latestCertificates: certs,
        totalCount: latestCertificates?.totalCount,
        actuality,
      })
    );
  } catch (error) {
    console.error(new Error(error as any));
    return new Response(
      JSON.stringify({
        entities: null,
        latestCertificates: null,
        actuality: null,
      })
    );
  }
};

export default topCertificationsInterceptor;
